/**
 * Iter 85 — Anticoagulation & Antiplatelet Management Framework page.
 *
 * Lite decision engine + reference library + documentation log + reception
 * scripts. Aligned with SIR / CIRSE / ASRA / RANZCR / THANZ guidance.
 *
 * Disclaimer (v1.0 Draft): every page carries the staff-guidance disclaimer
 * until the MDO signs off. Hold times appear in the reference table only —
 * the decision engine outputs an escalation tier, not specific numbers.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, ShieldCheck, BookOpen, ClipboardCheck, MessageCircle, Search, Copy, Stamp } from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = SECTIONS.anticoag;

const TIER_STYLE = {
  CONTINUE:             { bg: '#16A34A15', fg: '#15803D', label: 'Continue medication',         icon: CheckCircle2 },
  RADIOLOGIST_REVIEW:   { bg: '#D9770615', fg: '#B45309', label: 'Radiologist review required', icon: ShieldCheck },
  MANDATORY_ESCALATION: { bg: '#DC262615', fg: '#B91C1C', label: 'Mandatory escalation',        icon: AlertTriangle },
};

export default function AnticoagPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'tool';
  const [tab, setTab] = useState(initialTab);
  const [library, setLibrary] = useState(null);

  useEffect(() => {
    axios.get(`${API}/anticoag/library`, { withCredentials: true })
      .then(r => setLibrary(r.data))
      .catch(() => setLibrary({ procedures: [], medications: [], conditions: [] }));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <PageHeader library={library} />
        <Disclaimer />
        <Tabs tab={tab} setTab={setTab} />
        {!library ? (
          <p className="text-sm text-muted-foreground mt-6">Loading anticoag library…</p>
        ) : tab === 'tool' ? (
          <DecisionTool library={library} />
        ) : tab === 'reference' ? (
          <Reference library={library} />
        ) : tab === 'log' ? (
          <DecisionLog library={library} />
        ) : (
          <ReceptionScripts library={library} />
        )}
      </div>
    </div>
  );
}

function PageHeader({ library }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-3">
      <div>
        <div className="flex items-baseline gap-3">
          <h1 className="text-3xl font-bold text-foreground tracking-tight" data-testid="anticoag-title">
            Anticoagulation & Antiplatelet Framework
          </h1>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${C.color}15`, color: C.color }}
                data-testid="anticoag-version-pill">
            v{library?.framework_version || '2.0'} · {library?.framework_status || 'Approved'}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {library?.guideline_sources || 'SIR (2024) · Australian CEC · ASRA neuraxial · RANZCR · THANZ'} · DIAS-ready audit trail
        </p>
      </div>
      <Link to="/anticoag-screening"
        data-testid="anticoag-open-screening"
        className="shrink-0 text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white"
        style={{ backgroundColor: C.color }}>
        <ClipboardCheck className="w-4 h-4" /> Reception screening · 5-step
      </Link>
    </div>
  );
}

function Disclaimer() {
  return (
    <div className="rounded-xl border-2 p-3 mb-5 flex items-start gap-2 print:hidden"
         style={{ borderColor: `${C.color}50`, backgroundColor: `${C.color}08` }}
         data-testid="anticoag-disclaimer">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.color }} />
      <div className="text-[11px]">
        <p className="font-semibold mb-1" style={{ color: C.color }}>STAFF GUIDANCE — Not clinical authority.</p>
        <p className="text-foreground">
          This tool supports pre-procedural screening only. Reception/admin staff must <strong>NEVER</strong> independently
          instruct patients to cease anticoagulants. Final decisions rest with the procedural radiologist
          and/or the patient&apos;s treating clinician. Patients should be told:{' '}
          <em>&ldquo;Please continue your medication unless specifically advised otherwise by your treating doctor or our radiologist.&rdquo;</em>
        </p>
      </div>
    </div>
  );
}

function Tabs({ tab, setTab }) {
  const TABS = [
    { id: 'tool',      label: 'Decision tool', icon: ClipboardCheck },
    { id: 'reference', label: 'Reference library', icon: BookOpen },
    { id: 'log',       label: 'Documentation log', icon: Stamp },
    { id: 'scripts',   label: 'Reception scripts', icon: MessageCircle },
  ];
  return (
    <div className="flex gap-1 border-b border-border mb-6" data-testid="anticoag-tabs">
      {TABS.map(t => {
        const active = tab === t.id;
        const Icon = t.icon;
        return (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`anticoag-tab-${t.id}`}
            className="px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors"
            style={active
              ? { borderColor: C.color, color: C.color }
              : { borderColor: 'transparent', color: 'hsl(var(--muted-foreground))' }}>
            <Icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ──────────────────────────────── Decision tool ───────────────────────────
function DecisionTool({ library }) {
  const [procId, setProcId] = useState('');
  const [meds, setMeds] = useState([]);
  const [conds, setConds] = useState([]);
  const [egfr, setEgfr] = useState('');
  const [filter, setFilter] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // patient context (Section 11 — documentation)
  const [patientName, setPatientName] = useState('');
  const [indication, setIndication] = useState('');
  const [managingDoctor, setManagingDoctor] = useState('');
  const [instructionsGiven, setInstructionsGiven] = useState('');
  const [logged, setLogged] = useState(null);

  const procs = useMemo(() => {
    const f = filter.toLowerCase();
    return library.procedures.filter(p => !f || p.label.toLowerCase().includes(f));
  }, [library, filter]);

  const proc = library.procedures.find(p => p.id === procId);
  const dabigatranSelected = meds.includes('dabigatran');

  // Live engine — re-runs whenever inputs change so the badge updates without
  // needing a click. We still hit the API for the canonical reasons text.
  useEffect(() => {
    if (!procId) { setResult(null); return; }
    setBusy(true);
    const body = {
      procedure_id: procId,
      medication_ids: meds,
      conditions: conds,
      egfr: egfr ? parseFloat(egfr) : null,
    };
    axios.post(`${API}/anticoag/evaluate`, body, { withCredentials: true })
      .then(r => setResult(r.data))
      .catch(() => setResult(null))
      .finally(() => setBusy(false));
  }, [procId, meds, conds, egfr]);

  const tier = result?.tier;
  const TierIcon = tier ? TIER_STYLE[tier].icon : null;

  const logDecision = async () => {
    if (!procId) return;
    const r = await axios.post(`${API}/anticoag/decisions`, {
      procedure_id: procId, medication_ids: meds, conditions: conds,
      egfr: egfr ? parseFloat(egfr) : null,
      patient_name: patientName, indication, managing_doctor: managingDoctor,
      instructions_given: instructionsGiven,
    }, { withCredentials: true });
    setLogged(r.data);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="anticoag-tool">
      {/* LEFT — inputs */}
      <div className="space-y-4">
        <Card title="1 · Procedure" testid="anticoag-step-procedure">
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <input value={filter} onChange={e => setFilter(e.target.value)}
              data-testid="anticoag-proc-filter"
              placeholder="Filter procedures…"
              className="w-full text-sm bg-background border border-border rounded-lg pl-8 pr-3 py-1.5" />
          </div>
          <select value={procId} onChange={e => setProcId(e.target.value)}
            data-testid="anticoag-proc-select"
            className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2">
            <option value="">— pick a procedure —</option>
            {['low', 'moderate', 'high'].map(r => (
              <optgroup key={r} label={`${r.charAt(0).toUpperCase()}${r.slice(1)} bleeding risk`}>
                {procs.filter(p => p.risk === r).map(p => (
                  <option key={p.id} value={p.id}>{p.label}{p.spine ? ' · SPINE' : ''}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {proc && (
            <div className="mt-2 flex items-center gap-2">
              <RiskChip risk={proc.risk} />
              {proc.spine && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: '#DC262615', color: '#B91C1C' }}>Spine</span>
              )}
              {proc.consent_template_id && (
                <Link to={`/consent/${proc.consent_template_id}`}
                  className="ml-auto text-[11px] font-semibold underline-offset-2 hover:underline"
                  style={{ color: C.color }}
                  data-testid="anticoag-jump-to-consent">
                  Open matching consent →
                </Link>
              )}
            </div>
          )}
        </Card>

        <Card title="2 · Medications declared by patient" testid="anticoag-step-meds">
          <div className="flex flex-wrap gap-1.5" data-testid="anticoag-meds-chips">
            {library.medications.map(m => {
              const active = meds.includes(m.id);
              return (
                <button key={m.id} type="button"
                  onClick={() => setMeds(active ? meds.filter(x => x !== m.id) : [...meds, m.id])}
                  data-testid={`anticoag-med-${m.id}`}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors"
                  style={active
                    ? { backgroundColor: `${C.color}15`, borderColor: C.color, color: C.color }
                    : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                  {m.name}{m.brand ? ` (${m.brand})` : ''}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            Reception should also document the indication, last dose, and managing doctor below.
          </p>
        </Card>

        <Card title="2b · Renal function (eGFR)"
              subtitle="Required when dabigatran (Pradaxa) is declared — branches the hold time."
              testid="anticoag-step-egfr">
          <div className={`rounded-lg border p-2.5 ${dabigatranSelected ? 'border-2' : ''}`}
               style={dabigatranSelected
                 ? { borderColor: '#DC2626', backgroundColor: '#DC262608' }
                 : { borderColor: 'hsl(var(--border))' }}>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: dabigatranSelected ? '#B91C1C' : 'hsl(var(--muted-foreground))' }}>
                eGFR (mL/min/1.73m²) {dabigatranSelected && '· REQUIRED for Pradaxa'}
              </span>
              <input type="number" step="1" min="1" max="200"
                value={egfr} onChange={e => setEgfr(e.target.value)}
                data-testid="anticoag-egfr"
                placeholder="e.g. 65"
                className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </label>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Bucket boundaries: ≥ 80 · 50–79 · 30–49 · &lt; 30. Leave blank if not available — engine will escalate.
            </p>
          </div>
        </Card>

        <Card title="3 · High-risk clinical conditions"
              subtitle="Tick any that apply — any of these forces escalation."
              testid="anticoag-step-conds">
          <div className="grid grid-cols-1 gap-1.5" data-testid="anticoag-conds-list">
            {library.conditions.map(c => (
              <label key={c.id} className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={conds.includes(c.id)}
                  onChange={e => setConds(e.target.checked ? [...conds, c.id] : conds.filter(x => x !== c.id))}
                  data-testid={`anticoag-cond-${c.id}`}
                  className="w-4 h-4 mt-0.5 rounded border-border" />
                <span className="text-xs text-foreground">{c.label}</span>
              </label>
            ))}
          </div>
        </Card>

        <Card title="4 · Documentation fields"
              subtitle="Required per Section 11 — recorded with the decision in the audit log."
              testid="anticoag-step-docs">
          <div className="grid grid-cols-2 gap-2">
            <MiniField label="Patient name" value={patientName} onChange={setPatientName} testid="anticoag-patient-name" />
            <MiniField label="Managing doctor" value={managingDoctor} onChange={setManagingDoctor} testid="anticoag-mgmt-doctor"
              placeholder="GP / cardiologist" />
          </div>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Indication</span>
            <select value={indication} onChange={e => setIndication(e.target.value)}
              data-testid="anticoag-indication"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
              <option value="">—</option>
              <option value="af">Atrial fibrillation</option>
              <option value="stroke_prevention">Stroke prevention</option>
              <option value="heart_valve">Heart valve</option>
              <option value="coronary_stent">Coronary stent</option>
              <option value="dvt">DVT</option>
              <option value="pe">Pulmonary embolism</option>
              <option value="vascular_disease">Vascular disease</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <MiniField label="Instructions given to patient (optional)"
            value={instructionsGiven} onChange={setInstructionsGiven}
            testid="anticoag-instructions-given"
            placeholder="e.g. asked to continue meds, awaiting radiologist call" />
        </Card>
      </div>

      {/* RIGHT — output */}
      <div className="space-y-4">
        <Card title="Engine output" subtitle={busy ? 'Recomputing…' : '—'} testid="anticoag-output-card">
          {!procId ? (
            <p className="text-sm text-muted-foreground">Pick a procedure to see the recommendation.</p>
          ) : !result ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <div className="rounded-xl p-4 border-2 flex items-center gap-3"
                   style={{ backgroundColor: TIER_STYLE[tier].bg, borderColor: TIER_STYLE[tier].fg }}
                   data-testid={`anticoag-tier-${tier}`}>
                <TierIcon className="w-6 h-6 shrink-0" style={{ color: TIER_STYLE[tier].fg }} />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: TIER_STYLE[tier].fg }}>
                    Recommendation
                  </p>
                  <p className="text-lg font-bold" style={{ color: TIER_STYLE[tier].fg }}>
                    {TIER_STYLE[tier].label}
                  </p>
                  {result?.procedure?.category_label && (
                    <p className="text-[11px]" style={{ color: TIER_STYLE[tier].fg }}>
                      {result.procedure.category_label}
                    </p>
                  )}
                </div>
              </div>

              {result?.radiologist_confirmation_required && (
                <div className="rounded-lg border-2 p-2.5 flex items-start gap-2"
                     style={{ borderColor: '#B45309', backgroundColor: '#D9770610' }}
                     data-testid="anticoag-radiologist-banner">
                  <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#B45309' }} />
                  <p className="text-[11px] text-foreground">
                    <strong style={{ color: '#B45309' }}>RADIOLOGIST MUST CONFIRM</strong> — these are policy-derived
                    recommendations. The procedural radiologist has final authority and must sign off before any medication is held.
                  </p>
                </div>
              )}

              <div data-testid="anticoag-reasons">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Why?</p>
                <ul className="text-xs text-foreground space-y-1">
                  {result.reasons.map((r, i) => <li key={i}>• {r}</li>)}
                </ul>
              </div>

              <div className="rounded-lg p-3 border" style={{ borderColor: `${C.color}40`, backgroundColor: `${C.color}06` }}
                   data-testid="anticoag-patient-script">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: C.color }}>
                  Patient script (auto-generated)
                </p>
                <p className="text-xs text-foreground italic">{result.patient_script}</p>
                <button onClick={() => navigator.clipboard.writeText(result.patient_script)}
                  data-testid="anticoag-copy-script"
                  className="mt-2 text-[10px] font-semibold inline-flex items-center gap-1"
                  style={{ color: C.color }}>
                  <Copy className="w-3 h-3" /> Copy script
                </button>
              </div>

              <div className="pt-2 border-t border-border">
                <button onClick={logDecision}
                  data-testid="anticoag-log-btn"
                  disabled={!procId || !!logged}
                  className="w-full text-sm font-semibold py-2 rounded-lg text-white disabled:opacity-50"
                  style={{ backgroundColor: C.color }}>
                  {logged ? '✓ Decision logged to audit trail' : 'Log this decision to audit trail'}
                </button>
                {logged && (
                  <p className="text-[10px] text-muted-foreground mt-1.5 text-center"
                     data-testid="anticoag-logged-confirm">
                    Decision <code>{logged.decision_id}</code> · tier <strong>{logged.tier}</strong>
                  </p>
                )}
              </div>
            </>
          )}
        </Card>

        {/* Per-drug concrete hold/restart recommendations (v2.0). */}
        {result?.medication_recommendations?.length > 0 && (
          <Card title="Per-drug recommendation (concrete hold / restart)"
                subtitle={`Category ${result?.procedure?.category || '—'} · Radiologist final authority`}
                testid="anticoag-inline-med-recs">
            <div className="space-y-3">
              {result.medication_recommendations.map(m => (
                <div key={m.id} className="border-l-2 pl-3 py-1"
                     style={{ borderColor: C.color }}
                     data-testid={`anticoag-rec-${m.id}`}>
                  <p className="text-xs font-bold text-foreground">
                    {m.name}{m.brand ? ` (${m.brand})` : ''}
                    <span className="text-[10px] text-muted-foreground font-normal ml-1.5">
                      · {m.drug_class}
                    </span>
                  </p>
                  <p className="text-[11px] text-foreground mt-0.5">
                    <strong>Hold pre:</strong> {m.hold_pre}
                  </p>
                  <p className="text-[11px] text-foreground">
                    <strong>Restart post:</strong> {m.restart_post}
                  </p>
                  {m.renal_note && (
                    <p className="text-[10px] italic text-muted-foreground mt-0.5">
                      🧪 {m.renal_note}
                    </p>
                  )}
                  {m.reversal_agent && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      <strong>Reversal:</strong> {m.reversal_agent}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────── Reference tab ────────────────────────────
function Reference({ library }) {
  return (
    <div className="space-y-5" data-testid="anticoag-reference">
      <Card title="Master anticoagulation table — concrete hold / restart by Category" testid="anticoag-master-table">
        <div className="overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-2">Drug</th>
                <th className="py-2 pr-2">Class</th>
                <th className="py-2 pr-2" style={{ color: '#15803D' }}>Cat 1 — Low</th>
                <th className="py-2 pr-2" style={{ color: '#B45309' }}>Cat 2 — Moderate</th>
                <th className="py-2 pr-2" style={{ color: '#B91C1C' }}>Cat 3 — High</th>
                <th className="py-2 pr-2">Reversal</th>
              </tr>
            </thead>
            <tbody className="text-foreground">
              {library.medications.map(m => (
                <tr key={m.id} className="border-b border-border/40 align-top"
                    data-testid={`anticoag-master-row-${m.id}`}>
                  <td className="py-2 pr-2 font-semibold whitespace-nowrap">
                    {m.name}{m.brand ? <div className="text-[10px] text-muted-foreground font-normal">{m.brand}</div> : null}
                  </td>
                  <td className="py-2 pr-2 text-muted-foreground text-[11px]">{m.drug_class}</td>
                  {['1', '2', '3'].map(cat => (
                    <td key={cat} className="py-2 pr-2 text-[11px]">
                      <div><strong>Hold:</strong> {(m.hold || {})[cat]?.hold_pre || '—'}</div>
                      <div className="text-muted-foreground"><strong>Restart:</strong> {(m.hold || {})[cat]?.restart_post || '—'}</div>
                    </td>
                  ))}
                  <td className="py-2 pr-2 text-[11px] text-muted-foreground">{m.reversal_agent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-muted-foreground mt-3">
          Aligned with SIR (2024), Australian CEC, ASRA neuraxial, RANZCR, THANZ. Radiologist may override per patient.
        </p>
      </Card>

      {/* Dabigatran renal matrix — pulled out because it's eGFR-branched */}
      <Card title="Dabigatran (Pradaxa) — renal-function matrix"
            subtitle="Hold time depends on eGFR (mL/min/1.73m²). Restart per Category row above."
            testid="anticoag-dabigatran-matrix">
        {(() => {
          const dab = library.medications.find(m => m.id === 'dabigatran');
          if (!dab?.renal_matrix) return <p className="text-xs text-muted-foreground">—</p>;
          const buckets = [['ge80', '≥ 80'], ['50_79', '50–79'], ['30_49', '30–49'], ['lt30', '< 30']];
          return (
            <div className="overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-2">eGFR (mL/min)</th>
                    <th className="py-2 pr-2" style={{ color: '#15803D' }}>Cat 1</th>
                    <th className="py-2 pr-2" style={{ color: '#B45309' }}>Cat 2</th>
                    <th className="py-2 pr-2" style={{ color: '#B91C1C' }}>Cat 3</th>
                  </tr>
                </thead>
                <tbody className="text-foreground">
                  {buckets.map(([key, label]) => (
                    <tr key={key} className="border-b border-border/40 align-top"
                        data-testid={`anticoag-dab-row-${key}`}>
                      <td className="py-1.5 pr-2 font-semibold">{label}</td>
                      <td className="py-1.5 pr-2 text-[11px]">{dab.renal_matrix['1']?.[key]}</td>
                      <td className="py-1.5 pr-2 text-[11px]">{dab.renal_matrix['2']?.[key]}</td>
                      <td className="py-1.5 pr-2 text-[11px]">{dab.renal_matrix['3']?.[key]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Card>

      <Card title="Procedure bleeding-risk categories" testid="anticoag-proc-table">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {[
            [1, 'low',      'Category 1 — Low bleeding risk',      '#16A34A'],
            [2, 'moderate', 'Category 2 — Moderate bleeding risk', '#D97706'],
            [3, 'high',     'Category 3 — High bleeding risk',     '#DC2626'],
          ].map(([cat, r, t, color]) => (
            <div key={r} className="rounded-lg border p-3"
                 style={{ borderColor: `${color}40`, backgroundColor: `${color}06` }}
                 data-testid={`anticoag-proc-cat-${cat}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color }}>{t}</p>
              <ul className="text-[11px] text-foreground space-y-0.5">
                {library.procedures.filter(p => p.risk === r).map(p => (
                  <li key={p.id}>• {p.label}{p.spine ? ' · SPINE' : ''}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Card>

      <Card title="High-risk clinical conditions — automatic escalation" testid="anticoag-cond-table">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
          {library.conditions.map(c => (
            <li key={c.id} className="text-foreground">• {c.label}</li>
          ))}
        </ul>
      </Card>

      {/* Reversal agents (v2.0) */}
      {library.reversal_agents?.length > 0 && (
        <Card title="Reversal agents — urgent bleeding scenarios"
              subtitle="Do NOT use routine PT/aPTT to gauge DOAC activity — see specific-assay guidance below."
              testid="anticoag-reversal-agents">
          <div className="overflow-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-2">Drug group</th>
                  <th className="py-1.5 pr-2">Reversal agent</th>
                  <th className="py-1.5 pr-2">Dose</th>
                  <th className="py-1.5 pr-2">Notes</th>
                </tr>
              </thead>
              <tbody className="text-foreground">
                {library.reversal_agents.map((r, i) => (
                  <tr key={i} className="border-b border-border/40 align-top"
                      data-testid={`anticoag-reversal-row-${i}`}>
                    <td className="py-1.5 pr-2 font-semibold text-[11px]">{r.drug_group}</td>
                    <td className="py-1.5 pr-2 text-[11px]">{r.agent}</td>
                    <td className="py-1.5 pr-2 text-[11px] text-muted-foreground">{r.dose}</td>
                    <td className="py-1.5 pr-2 text-[11px] text-muted-foreground">{r.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Coag test advisory (SIR 2024) */}
      {library.coag_test_advisory && (
        <Card title="Coagulation testing — SIR 2024 advisory" testid="anticoag-coag-advisory">
          <div className="rounded-lg border-2 p-3"
               style={{ borderColor: '#B45309', backgroundColor: '#D9770610' }}>
            <p className="text-xs text-foreground mb-2">
              <strong style={{ color: '#B45309' }}>⚠ Routine tests unreliable:</strong> {library.coag_test_advisory.warning}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#B45309' }}>
              Specific assays if urgent measurement needed:
            </p>
            <ul className="text-[11px] text-foreground space-y-0.5">
              {(library.coag_test_advisory.specific_assays || []).map((a, i) => (
                <li key={i}>• {a}</li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      <Card title="Medico-legal footer & governance" testid="anticoag-medicolegal">
        <p className="text-xs text-foreground">
          These recommendations provide policy-derived guidance and do not replace
          individualised clinical assessment by the treating radiologist or referring
          clinician. Anticoagulation management protocols are based on contemporary
          consensus guidance including SIR (2024) Consensus Guidelines, Australian
          Clinical Excellence Commission (CEC), ASRA neuraxial guidelines, RANZCR
          Standards, and THANZ position statements — adapted to local procedural practice.
        </p>
        <p className="text-[10px] text-muted-foreground mt-2">
          Framework version: <strong>{library.framework_version}</strong> · Status: <strong>{library.framework_status}</strong> · Sources: {library.guideline_sources}
        </p>
      </Card>
    </div>
  );
}

// ────────────────────────────── Documentation log tab ────────────────────
function DecisionLog({ library }) {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [rows, setRows] = useState([]);
  const [tier, setTier] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [stats, setStats] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (tier) params.set('tier', tier);
    if (pendingOnly) params.set('pending_approval', '1');
    axios.get(`${API}/anticoag/decisions?${params.toString()}`, { withCredentials: true })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]));
    axios.get(`${API}/anticoag/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(() => setStats(null));
  };

  useEffect(load, [tier, pendingOnly]);

  if (!isPM) {
    return (
      <Card title="Documentation log" testid="anticoag-log-noaccess">
        <p className="text-sm text-muted-foreground">
          The full decision log is available to Practice Managers and Admins only.
          Your own logged decisions appear on the patient&apos;s consent record.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="anticoag-log">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Kpi label="Total decisions"     value={stats?.total ?? '—'} testid="anticoag-kpi-total" />
        <Kpi label="Continue"            value={stats?.by_tier?.CONTINUE ?? 0} testid="anticoag-kpi-continue" color="#15803D" />
        <Kpi label="Radiologist review"  value={stats?.by_tier?.RADIOLOGIST_REVIEW ?? 0} testid="anticoag-kpi-review" color="#B45309" />
        <Kpi label="Pending approval"    value={stats?.pending_approval ?? 0} testid="anticoag-kpi-pending" color="#B91C1C" />
      </div>

      <Card title="Decisions" testid="anticoag-log-table">
        <div className="flex items-center gap-2 mb-3">
          <select value={tier} onChange={e => setTier(e.target.value)}
            data-testid="anticoag-log-tier-filter"
            className="text-xs bg-background border border-border rounded-lg px-2 py-1">
            <option value="">All tiers</option>
            <option value="CONTINUE">Continue</option>
            <option value="RADIOLOGIST_REVIEW">Radiologist review</option>
            <option value="MANDATORY_ESCALATION">Mandatory escalation</option>
          </select>
          <label className="text-xs flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={pendingOnly} onChange={e => setPendingOnly(e.target.checked)}
              data-testid="anticoag-log-pending-only" />
            Pending approval only
          </label>
          <button onClick={load} className="ml-auto text-xs font-semibold underline-offset-2 hover:underline"
            data-testid="anticoag-log-refresh">Refresh</button>
        </div>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="anticoag-log-empty">No decisions logged yet.</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-2">When</th>
                  <th className="py-1.5 pr-2">Patient</th>
                  <th className="py-1.5 pr-2">Procedure</th>
                  <th className="py-1.5 pr-2">Meds</th>
                  <th className="py-1.5 pr-2">Tier</th>
                  <th className="py-1.5 pr-2">Logged by</th>
                  <th className="py-1.5 pr-2">Radiologist</th>
                </tr>
              </thead>
              <tbody className="text-foreground">
                {rows.map(r => (
                  <tr key={r.decision_id} className="border-b border-border/40 align-top"
                      data-testid={`anticoag-log-row-${r.decision_id}`}>
                    <td className="py-1.5 pr-2 text-muted-foreground">
                      {r.logged_at ? new Date(r.logged_at).toLocaleString('en-AU') : '—'}
                    </td>
                    <td className="py-1.5 pr-2">{r.patient_name || '—'}</td>
                    <td className="py-1.5 pr-2">{r.procedure?.label || r.procedure_id}</td>
                    <td className="py-1.5 pr-2">{(r.medications || []).map(m => m.name).join(', ') || '—'}</td>
                    <td className="py-1.5 pr-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: TIER_STYLE[r.tier]?.bg, color: TIER_STYLE[r.tier]?.fg }}>
                        {r.tier?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-muted-foreground">{r.logged_by?.name || '—'}</td>
                    <td className="py-1.5 pr-2">
                      {r.radiologist_approval ? (
                        <span style={{ color: '#15803D' }}>✓ {r.radiologist_name || 'approved'}</span>
                      ) : r.tier === 'CONTINUE' ? (
                        <span className="text-muted-foreground">— not required</span>
                      ) : (
                        <ApproveBtn decisionId={r.decision_id} onDone={load} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ApproveBtn({ decisionId, onDone }) {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/anticoag/decisions/${decisionId}`,
        { radiologist_approval: 'approved' }, { withCredentials: true });
      onDone();
    } catch (_e) { /* ignore */ }
    setBusy(false);
  };
  return (
    <button onClick={approve} disabled={busy}
      data-testid={`anticoag-approve-${decisionId}`}
      className="text-[10px] font-semibold px-2 py-0.5 rounded border"
      style={{ borderColor: '#16A34A40', color: '#15803D' }}>
      {busy ? '…' : 'Approve'}
    </button>
  );
}

// ───────────────────────────── Reception scripts tab ─────────────────────
function ReceptionScripts({ library }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="anticoag-scripts">
      <Card title="❌ Reception must NEVER say" testid="anticoag-never">
        <ul className="space-y-2 text-sm">
          {library.reception_never.map((line, i) => (
            <li key={i} className="rounded-lg p-2.5 border" style={{ borderColor: '#DC262640', backgroundColor: '#DC262608' }}>
              <span className="text-foreground">&ldquo;{line}&rdquo;</span>
            </li>
          ))}
        </ul>
      </Card>
      <Card title="✅ Reception ALWAYS say" testid="anticoag-always">
        <ul className="space-y-2 text-sm">
          {library.reception_always.map((line, i) => (
            <li key={i} className="rounded-lg p-2.5 border" style={{ borderColor: '#16A34A40', backgroundColor: '#16A34A08' }}>
              <span className="text-foreground">&ldquo;{line}&rdquo;</span>
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Tier-by-tier patient scripts" testid="anticoag-patient-scripts">
        {Object.entries(library.patient_scripts).map(([tier, script]) => {
          const T = TIER_STYLE[tier];
          return (
            <div key={tier} className="rounded-lg p-3 border" style={{ borderColor: `${T.fg}40`, backgroundColor: T.bg }}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: T.fg }}>{T.label}</p>
              <p className="text-xs text-foreground italic">&ldquo;{script}&rdquo;</p>
              <button onClick={() => navigator.clipboard.writeText(script)}
                data-testid={`anticoag-copy-${tier}`}
                className="mt-1.5 text-[10px] font-semibold inline-flex items-center gap-1" style={{ color: T.fg }}>
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ────────────────────────────── shared mini-components ───────────────────
function Card({ title, subtitle, children, testid }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid={testid}>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>{title}</p>
        {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function MiniField({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
    </label>
  );
}

function Kpi({ label, value, color, testid }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-3" data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color: color || 'hsl(var(--foreground))' }}>{value}</p>
    </div>
  );
}

function RiskChip({ risk }) {
  const colors = { low: ['#16A34A15', '#15803D'], moderate: ['#D9770615', '#B45309'], high: ['#DC262615', '#B91C1C'] };
  const [bg, fg] = colors[risk] || colors.low;
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{ backgroundColor: bg, color: fg }}>{risk} bleeding risk</span>
  );
}
